import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { camBasis, createWorldObject, updateWorldObject, deleteWorldObject, listWorldObjects } from '../services/worldObjects';
import { seedsFor } from '../hooks/useRoomPlaceables';
import { STANDING_EYE } from '../services/seats';
import { ROOMS } from '../rooms';

// PlaceablesPanel — ONE tool for everything hung in a room.
//
// The record sleeves proved the machinery (RecordCardsPanel + the worldObjects
// slice); this generalizes it across kinds, which is the correction Khari
// named: "we have been building them separate for rooms, and that is the bad
// design." Every placeable in the current room is detected, NUMBERED (the
// in-scene badges show the same numbers — PlaceableBadges), and edited with
// the same touch-friendly pad. Save writes world_objects rows; rows beat
// rooms.js, so arranging a room is a save, never a paste or a deploy.
//
// Works in ANY interior. A room with nothing placed still offers "+ video
// panel" — which is how any room becomes meet-capable on the spot.

const KINDS = [
  { kind: 'video_panel', label: 'Video panel', billboard: true, addable: true, defSize: 1.4 },
  { kind: 'plaque', label: 'Wall records', addable: true, defSize: 0.42 },
  { kind: 'result_card', label: 'Result cards', addable: true, defSize: 0.55 },
  { kind: 'card', label: 'Sleeves', addable: true, defSize: 0.5 },
  // SEATS — where somebody SITS, and therefore how many a listening room holds
  // (project_world_listening_rooms). A built room like the salon carries seat
  // affordances on its model and needs none of these; a SCANNED room carries
  // nothing readable at all, so its seats are dropped by hand.
  //
  // Khari, 2026-08-04, standing in the Listening Room: "the room has 3 seats
  // available + 1 for me. 2 on the couch.. one on the chair.. the other is
  // where I am placed in this image." Nobody can derive that from a point
  // cloud — you go and stand where the cushion is.
  //
  // ORDER IS THE SEAT NUMBER: the server hands out an index, so `slot` (then
  // object_id) decides which chair is which, identically on every client. Add
  // them in the order you want them filled.
  { kind: 'seat', label: 'Seats', addable: true, defSize: 0.4 },
];

const safe = (side, base) => `max(${base}px, env(safe-area-inset-${side}))`;
const panel = {
  position: 'fixed', right: safe('right', 16), top: `calc(${safe('top', 16)} + 200px)`, zIndex: 40,
  width: 232, maxHeight: '70vh', overflowY: 'auto', pointerEvents: 'auto', color: '#ffe9c4',
  font: '12.5px system-ui, sans-serif',
  background: 'rgba(16,10,6,0.82)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 12,
  padding: '12px 12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
};
const btn = {
  pointerEvents: 'auto', background: 'rgba(255,178,94,0.14)', color: '#ffd27d',
  border: '1px solid rgba(255,178,94,0.4)', borderRadius: 8, padding: '7px 0', cursor: 'pointer',
  font: '600 13px system-ui', textAlign: 'center', userSelect: 'none',
};
const arrow = { ...btn, padding: '9px 0' };
const r2 = (n) => Math.round(n * 100) / 100;
// Body height is tuned in centimetres-of-a-metre; two places would quantise the
// fine step (0.01) into the coarse one.
const r3 = (n) => Math.round(n * 1000) / 1000;

// One body shape for every kind — data rides along so a panel's billboard flag
// survives the round trip.
const bodyOf = (o) => ({
  zone_id: o.zone_id, kind: o.kind, ref: o.ref ?? null, slot: o.slot ?? 0,
  pos: o.pos, rotY: o.rotY, size: o.size, data: o.data ?? null,
});

export default function PlaceablesPanel() {
  const ON = useWorldStore((s) => s.adminTool) === 'placeables';
  const interior = useWorldStore((s) => s.interior);
  const objects = useWorldStore((s) => s.worldObjects);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const dirty = useWorldStore((s) => s.worldObjectDirty);
  const setSel = useWorldStore((s) => s.setWorldObjectSel);
  const patch = useWorldStore((s) => s.patchWorldObject);
  const addObj = useWorldStore((s) => s.addWorldObject);
  const removeObj = useWorldStore((s) => s.removeWorldObject);
  const markSaved = useWorldStore((s) => s.markWorldObjectSaved);
  const setObjects = useWorldStore((s) => s.loadWorldObjectSlice);

  const [fine, setFine] = useState(false);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // Populate every kind's slice for this room when the tool opens. The room's
  // own renderers already load what they draw; this covers kinds the room has
  // never had (so "+ add" starts from an empty, LOADED slice, not a stale one).
  useEffect(() => {
    if (!ON || !interior) return undefined;
    let dead = false;
    KINDS.forEach(({ kind }) => {
      listWorldObjects(interior, kind).then((rows) => {
        if (dead) return;
        setObjects(interior, kind, rows.length ? rows : seedsFor(interior, kind));
      });
    });
    return () => { dead = true; };
  }, [ON, interior, setObjects]);

  if (!ON) return null;
  if (!interior) {
    return (
      <div style={panel}>
        <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8 }}>Room placeables</div>
        <div style={{ opacity: 0.65, fontSize: 11.5, lineHeight: 1.4 }}>Step into a room — this tool arranges what hangs in it.</div>
      </div>
    );
  }

  // Everything in this room, grouped by kind, numbered GLOBALLY (1..N) so the
  // in-scene badges and this list always agree on what "3" is.
  const groups = KINDS.map((k) => ({
    ...k,
    items: objects
      .filter((o) => o.zone_id === interior && o.kind === k.kind)
      .sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0)),
  }));
  let n = 0;
  const numbered = groups.map((g) => ({ ...g, items: g.items.map((o) => ({ ...o, badge: ++n })) }));
  const all = numbered.flatMap((g) => g.items);
  const cur = all.find((o) => o.object_id === sel) || null;
  const curKind = cur ? KINDS.find((k) => k.kind === cur.kind) : null;
  const step = fine ? 0.02 : 0.08;

  const edit = (fn) => cur && patch(cur.object_id, fn(cur));
  const move = (axis, dir) => edit((o) => {
    const [x, y, z] = o.pos; const d = step * dir;
    if (axis === 'across') return { pos: [r2(x + camBasis.rx * d), y, r2(z + camBasis.rz * d)] };
    if (axis === 'depth') return { pos: [r2(x + camBasis.fx * d), y, r2(z + camBasis.fz * d)] };
    return { pos: [x, r2(y + d), z] };
  });
  const turn = (dir) => edit((o) => ({ rotY: r2(o.rotY + 0.06 * dir) }));
  // ── FIXING A SEAT THAT FACES THE WRONG WAY ──────────────────────────────────
  //
  // A seat's `rotY` is WHERE THE SITTER LOOKS, captured from where YOU were
  // looking when you pressed ＋ — so it is right only if you stood on the
  // cushion and looked OUT. The natural authoring move is the opposite: you walk
  // up to the couch and look AT it, which stores a sitter staring into the
  // upholstery. Khari, 2026-08-06, sitting down in the Listening Room: "my
  // camera is flying to the correct seat, and then facing it instead of turning
  // around as me."
  //
  // At 0.06 rad a press, undoing that by nudging is ~52 presses, which is why
  // nobody does it and the seat just stays wrong. Two one-press answers:
  const flip = () => edit((o) => ({ rotY: r2(((o.rotY || 0) + Math.PI) % (Math.PI * 2)) }));
  // …and the general case: look the way the sitter should look, press it. Same
  // gesture as placing, minus having to stand in the chair to do it.
  const faceMyWay = () => edit(() => ({ rotY: r2(Math.atan2(camBasis.fx, camBasis.fz)) }));
  const resize = (dir) => edit((o) => ({ size: Math.max(0.12, r2(o.size + 0.04 * dir)) }));
  const setBillboard = (on) => edit((o) => ({ data: { ...(o.data || {}), billboard: on } }));

  // ── HOW BIG THE PERSON IN THIS CHAIR IS ─────────────────────────────────────
  //
  // Khari, 2026-08-07: "I need the admin panel to allow me to size them."
  //
  // Per SEAT, not per avatar, and deliberately NOT the seat's own `size` (that
  // is the floor footprint, and overloading it would mean resizing the marker
  // resized the person). roster.json carries one height per avatar, which cannot
  // be right for both an armchair and a low couch — the furniture is the thing
  // that knows, so the furniture holds the number.
  //
  // Unset falls back to the roster's height, so every seat that nobody has tuned
  // behaves exactly as it did before.
  const BODY_DEFAULT = 0.75;
  const bodyHeight = Number(cur?.data?.bodyHeight) || BODY_DEFAULT;
  const sizeBody = (dir) => edit((o) => ({
    data: {
      ...(o.data || {}),
      bodyHeight: Math.max(0.2, r3((Number(o.data?.bodyHeight) || BODY_DEFAULT) + (fine ? 0.01 : 0.04) * dir)),
    },
  }));
  const clearBodyHeight = () => edit((o) => {
    const d = { ...(o.data || {}) };
    delete d.bodyHeight;
    return { data: d };
  });

  // New things land in front of you at eye-ish height — never at the origin
  // inside a wall.
  //
  // A SEAT IS THE ONE EXCEPTION, and getting it wrong is invisible until
  // somebody sits: seats are stored at FLOOR level (services/seats.js —
  // `seatsFromObjects` adds the seated eye back on, and a body has the same
  // offset taken off to find its feet), so hanging one two units ahead at eye
  // height puts the chair a head above the room and the sitter in mid-air.
  //
  // So a seat is dropped AT YOUR FEET, FACING WHERE YOU FACE. That is also the
  // authoring gesture the whole placed-seat path was designed around — Khari,
  // standing in the Listening Room: "the other is where I am placed in this
  // image." You walk to the cushion, look where a sitter would look, press ＋.
  const add = (k) => {
    const g = numbered.find((x) => x.kind === k.kind);
    const seat = k.kind === 'seat';
    const pos = seat
      ? [r2(camBasis.px), r2(camBasis.py - STANDING_EYE), r2(camBasis.pz)]
      : [r2(camBasis.px + camBasis.fx * 2), r2(Math.max(1, camBasis.py)), r2(camBasis.pz + camBasis.fz * 2)];
    addObj({
      zone_id: interior, kind: k.kind, ref: null, slot: g.items.length, pos,
      // Facing convention, not camera convention — the seat's yaw is read
      // straight back as "the direction the sitter faces" (seats.js §ONE YAW
      // CONVENTION), and 0 is +z for both.
      rotY: seat ? r2(Math.atan2(camBasis.fx, camBasis.fz)) : 0,
      size: k.defSize,
      data: k.billboard ? { billboard: false } : null,
    });
  };

  const saveOne = async (o) => {
    const isNew = typeof o.object_id === 'string';
    const saved = isNew ? await createWorldObject(bodyOf(o)) : await updateWorldObject(o.object_id, bodyOf(o));
    if (saved?.object_id) {
      patch(o.object_id, { object_id: saved.object_id });
      markSaved(o.object_id); markSaved(saved.object_id);
      return saved.object_id;
    }
    return null;
  };

  const save = async () => {
    if (!cur) return;
    setBusy(true); setMsg('');
    try {
      const id = await saveOne(cur);
      if (id) setSel(id);
      setMsg('saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const saveRoom = async () => {
    setBusy(true); setMsg('');
    try {
      for (const o of all) if (typeof o.object_id === 'string' || dirty.includes(o.object_id)) await saveOne(o);
      setMsg('room saved');
    } catch (e) {
      setMsg(/500|Failed/.test(e.message) ? 'save failed — has migration 261 run?' : e.message);
    } finally { setBusy(false); setTimeout(() => setMsg(''), 3000); }
  };

  const remove = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      if (typeof cur.object_id === 'number') await deleteWorldObject(cur.object_id);
      removeObj(cur.object_id);
      setSel(null);
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };

  const isDirty = cur && (dirty.includes(cur.object_id) || typeof cur.object_id === 'string');

  return (
    <div style={panel}>
      <div style={{ fontSize: 10.5, letterSpacing: 1.3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>
        Room placeables · {ROOMS[interior]?.name || interior}
      </div>
      <div style={{ fontSize: 10.5, opacity: 0.55, marginBottom: 8, lineHeight: 1.35 }}>
        Numbers match the badges in the room — tap either to select.
      </div>

      {numbered.map((g) => (g.items.length || g.addable) && (
        <div key={g.kind} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 9.5, letterSpacing: 1.1, textTransform: 'uppercase', opacity: 0.5, margin: '6px 0 4px' }}>
            {g.label}
          </div>
          {/* The gesture, said once where it is needed — a seat is the only
              kind that lands where you STAND rather than in front of you, and
              nothing on screen would tell you that. */}
          {g.kind === 'seat' && (
            <div style={{ fontSize: 10, opacity: 0.5, lineHeight: 1.35, margin: '0 0 5px' }}>
              Stand on the cushion, look where a sitter looks, then ＋. Order = fill order.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {g.items.map((o) => (
              <button key={o.object_id} onClick={() => setSel(o.object_id)}
                style={{ ...btn, minWidth: 40, padding: '6px 0', fontSize: 12,
                  background: o.object_id === sel ? '#ffb25e' : 'rgba(255,178,94,0.14)',
                  color: o.object_id === sel ? '#1c1206' : '#ffd27d' }}>
                {o.badge}{(dirty.includes(o.object_id) || typeof o.object_id === 'string') ? '•' : ''}
              </button>
            ))}
            {g.addable && (
              <button style={{ ...btn, minWidth: 40, padding: '6px 8px', fontSize: 12, opacity: 0.8 }} onClick={() => add(g)}>＋</button>
            )}
          </div>
        </div>
      ))}

      {cur && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 5, margin: '10px 0 4px' }}>
            <span />
            <button style={arrow} onClick={() => move('height', 1)}>↑</button>
            <span />
            <button style={arrow} onClick={() => move('across', -1)}>←</button>
            <div style={{ display: 'grid', placeItems: 'center', fontSize: 10, opacity: 0.6 }}>height</div>
            <button style={arrow} onClick={() => move('across', 1)}>→</button>
            <span />
            <button style={arrow} onClick={() => move('height', -1)}>↓</button>
            <span />
          </div>

          <div style={{ display: 'flex', gap: 5, margin: '8px 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('depth', 1)}>▲ closer</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => move('depth', -1)}>further ▼</button>
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(1)}>⟲ rotate</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => turn(-1)}>rotate ⟳</button>
          </div>
          {/* Seats only: rotation on a seat is not decoration, it is where the
              sitter's head points, and it is the one property you cannot check
              by looking at the room. See `flip` / `faceMyWay`. */}
          {cur.kind === 'seat' && (
            <>
              <div style={{ display: 'flex', gap: 5, margin: '0 0 5px' }}>
                <button style={{ ...btn, flex: 1 }} onClick={flip} title="Sitter faces the opposite way">↻ flip 180°</button>
                <button style={{ ...btn, flex: 1 }} onClick={faceMyWay} title="Sitter looks the way you are looking now">face my way</button>
              </div>
              {/* The PERSON, not the seat marker. `− size` above changes the
                  floor ring; these change whoever is in the chair. */}
              <div style={{ display: 'flex', gap: 5, margin: '0 0 5px', alignItems: 'center' }}>
                <button style={{ ...btn, flex: 1 }} onClick={() => sizeBody(-1)} title="Smaller person in this chair">− person</button>
                <button style={{ ...btn, flex: 1 }} onClick={() => sizeBody(1)} title="Bigger person in this chair">person +</button>
                <button
                  style={{ ...btn, opacity: cur.data?.bodyHeight ? 1 : 0.4 }}
                  onClick={clearBodyHeight}
                  title="Back to the avatar's own height from roster.json"
                >reset</button>
              </div>
              <div style={{ font: '11px system-ui', opacity: 0.6, margin: '0 0 6px' }}>
                person {bodyHeight.toFixed(3)}m
                {cur.data?.bodyHeight ? '' : ' (roster default — not saved until you change it)'}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 5, margin: '0 0 8px' }}>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(-1)}>− size</button>
            <button style={{ ...btn, flex: 1 }} onClick={() => resize(1)}>size +</button>
          </div>

          {curKind?.billboard && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 6px', cursor: 'pointer', opacity: 0.9 }}>
              <input type="checkbox" checked={!!cur.data?.billboard} onChange={(e) => setBillboard(e.target.checked)} />
              billboard (face the viewer)
            </label>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 8px', cursor: 'pointer', opacity: 0.9 }}>
            <input type="checkbox" checked={fine} onChange={(e) => setFine(e.target.checked)} />
            fine steps
          </label>

          <div style={{ opacity: 0.5, fontSize: 10, marginBottom: 8, fontVariantNumeric: 'tabular-nums' }}>
            #{cur.badge} · {cur.pos.map(r2).join(', ')} · rot {r2(cur.rotY)} · {r2(cur.size)}
          </div>

          <div style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
            <button disabled={busy}
              style={{ ...btn, flex: 1, background: isDirty ? '#ffb25e' : 'rgba(255,178,94,0.14)', color: isDirty ? '#1c1206' : '#ffd27d' }}
              onClick={save}>{busy ? '…' : isDirty ? 'Save *' : 'Save'}</button>
            <button style={{ ...btn, width: 52 }} onClick={remove} disabled={busy}>del</button>
          </div>
        </>
      )}

      <button style={{ ...btn, width: '100%' }} onClick={saveRoom} disabled={busy}>save whole room</button>
      {msg && <div style={{ fontSize: 10.5, marginTop: 8, opacity: 0.85, color: /saved/.test(msg) ? '#9dff7d' : '#ff9d7d' }}>{msg}</div>}
    </div>
  );
}
